import React from "react";

import { useNavigate, useLocation } from "react-router-dom";

import { useTranslation } from "react-i18next";

import { useAuth0 } from "@auth0/auth0-react";

import "./PlatformSidebar.css";



import {

  House,

  FileText,

  Handshake,

  FolderOpen,

  MessagesSquare,

  UserPen,

  LockKeyhole,

  X,

} from "lucide-react";



import { getDossierDetail } from "../../../pages/homebuyer/dossier/steps/homebuyerDossierApi";

import { mapDossierDetailToDraft } from "../../../pages/homebuyer/dossier/steps/mapDossierDetailtoDraft";

import OssSituationModal from "../../../pages/homebuyer/dashboard/OssSituationModal";
import CommunicationUnreadBadge from "../../common/CommunicationUnreadBadge";
import useCommunicationUnreadCount from "../../common/useCommunicationUnreadCount";



const DOSSIER_STORAGE_KEY = "legup_hb_dossier_draft";

const DOSSIER_META_STORAGE_KEY = "legup_hb_dossier_meta";



/* =========================================================*

*   DOSSIER LOCK*

*   ========================================================= */



function isDossierLocked(summary = {}) {

  const loanDecision = String(summary?.loan_decision || "").toUpperCase();



  return Boolean(

    summary?.has_agreed_lender_result ||

    (summary?.has_lender_result && loanDecision === "APPROVED"),

  );

}



export default function PlatformSidebar({

  title,

  mobileOpen = false,

  onClose,

  dashboardSummary = null,

  onNavigateRequest,

}) {

  const { t } = useTranslation("homebuyer-sidebar");



  const navigate = useNavigate();

  const location = useLocation();



  const { getAccessTokenSilently } = useAuth0();

  const unreadMessagesCount = useCommunicationUnreadCount();



  const [ossSituationModalOpen, setOssSituationModalOpen] =

    React.useState(false);



  const [dossierLockedModalOpen, setDossierLockedModalOpen] =

    React.useState(false);



  const [dossierLockedModalClosing, setDossierLockedModalClosing] =

    React.useState(false);



  const goTo = React.useCallback(

    (path, state = null) => {

      if (onNavigateRequest) {

        setOssSituationModalOpen(false);

        setDossierLockedModalOpen(false);



        onNavigateRequest(path, state);

      } else {

        navigate(

          path,

          state

            ? {

                state,

              }

            : undefined,

        );

      }



      onClose?.();

    },

    [navigate, onNavigateRequest, onClose],

  );



  const openDossierLockedModal = React.useCallback(() => {

    setDossierLockedModalClosing(false);

    setDossierLockedModalOpen(true);

  }, []);



  const closeDossierLockedModal = React.useCallback(() => {

    if (!dossierLockedModalOpen || dossierLockedModalClosing) {

      return;

    }



    setDossierLockedModalClosing(true);

  }, [dossierLockedModalOpen, dossierLockedModalClosing]);



/* =========================================================*

*     DOSSIER NAVIGATION*

*     ========================================================= */



  const handleDossierNavigation = React.useCallback(async () => {

    const summary = dashboardSummary || {};



/* =======================================================*

*       AGREED / APPROVED LOAN*

*       ======================================================= */



    if (isDossierLocked(summary)) {

      openDossierLockedModal();



      onClose?.();



      return;

    }



    const hasDossier = Boolean(

      summary?.has_dossier ||

      summary?.has_draft_dossier ||

      summary?.has_submitted_dossier ||

      summary?.has_ready_for_export_dossier ||

      summary?.has_exported_dossier,

    );



    const dossierId = summary?.dossier_id;



/* =======================================================*

*       NO DOSSIER*

*       ======================================================= */



    if (!hasDossier || !dossierId) {

      goTo("/hb/dossier");

      return;

    }



/* =======================================================*

*       EXISTING EDITABLE DOSSIER*

*       ======================================================= */



    try {

      const accessToken = await getAccessTokenSilently();



      const dossier = await getDossierDetail({

        accessToken,

        dossierId,

      });



      const draft = mapDossierDetailToDraft(dossier);



      const personalInfoPath = "/hb/dossier/form/personal-info";



      const draftMeta = {

        status: draft?.status || summary?.dossier_status || "DRAFT",



        dossier_id: draft?.dossier_id || dossierId,



        dossier_code: draft?.dossier_code || summary?.dossier_code || null,



        dossier_applicant_id: draft?.dossier_applicant_id || null,



        current_step_key: "personal-info",

        current_step_label: "Personal Info",

        current_step_path: personalInfoPath,



        updated_at: new Date().toISOString(),

      };



      localStorage.setItem(DOSSIER_STORAGE_KEY, JSON.stringify(draft));



      localStorage.setItem(DOSSIER_META_STORAGE_KEY, JSON.stringify(draftMeta));



      goTo(personalInfoPath, {

        fromUpdate: true,

        dossierId,

        ts: Date.now(),

      });

    } catch (error) {

      console.error("Could not open dossier flow from sidebar:", error);



      goTo("/hb/dossier/form/personal-info", {

        fromUpdate: true,

        dossierId,

        loadError: true,

        ts: Date.now(),

      });

    }

  }, [

    dashboardSummary,

    getAccessTokenSilently,

    goTo,

    onClose,

    openDossierLockedModal,

  ]);



/* =========================================================*

*     OSS NAVIGATION*

*     ========================================================= */



  const handleOssNavigation = React.useCallback(() => {

    const summary = dashboardSummary || {};



    const ossStatus = String(

      summary?.oss_connection_status || "",

    ).toUpperCase();



/* =======================================================*

*       EXISTING ACTIVE OSS CONNECTION*

*       ======================================================= */



    const hasActiveOssConnection =

      Boolean(summary?.has_oss_connection) &&

      (ossStatus === "PENDING" || ossStatus === "ACCEPTED");



    if (hasActiveOssConnection) {

      goTo("/hb/oss/property-details", {

        dossierId: summary?.dossier_id || null,



        prefillFromDossier: true,



        skipSituationStep: true,



        propertySituation: "HAS_PROPERTY",



        requestType: summary?.oss_request_type || "RENOVATION",

      });



      return;

    }



/* =======================================================*

*       LOAN CASE / LENDER-CREATED DOSSIER*

*       ======================================================= */



    const hasLoanCase = Boolean(

      summary?.loan_case_id ||

      summary?.loan_case_reference ||

      summary?.has_lender_result ||

      summary?.dossier_origin === "LENDER_MANUAL",

    );



    if (hasLoanCase) {

      goTo("/hb/oss/property-details", {

        prefillFromDossier: true,



        dossierId: summary?.dossier_id || null,



        skipSituationStep: true,



        propertySituation: "HAS_PROPERTY",



        requestType: "RENOVATION",



        fromLoanCase: true,

      });



      return;

    }



/* =======================================================*

*       COMPLETED HOMEBUYER DOSSIER*

*       ======================================================= */



    const hasDossierPropertyInfo = Boolean(

      summary?.has_submitted_dossier ||

      summary?.has_ready_for_export_dossier ||

      summary?.has_exported_dossier ||

      summary?.dossier_status === "READY_FOR_EXPORT" ||

      summary?.dossier_status === "EXPORTED",

    );



    if (hasDossierPropertyInfo) {

      goTo("/hb/oss/property-details", {

        prefillFromDossier: true,



        dossierId: summary?.dossier_id || null,



        skipSituationStep: true,



        propertySituation: "HAS_PROPERTY",



        requestType: "RENOVATION",

      });



      return;

    }



/* =======================================================*

*       NO LOAN CASE / NO COMPLETED DOSSIER*

*       ======================================================= */



    setOssSituationModalOpen(true);



    onClose?.();

  }, [dashboardSummary, goTo, onClose]);



/* =========================================================*

*     OSS - HAS PROPERTY*

*     ========================================================= */



  const handleOssHasProperty = React.useCallback(() => {

    const summary = dashboardSummary || {};



    const hasLoanCase = Boolean(

      summary?.loan_case_id ||

      summary?.loan_case_reference ||

      summary?.has_lender_result ||

      summary?.dossier_origin === "LENDER_MANUAL",

    );



    const hasDossierPropertyInfo = Boolean(

      hasLoanCase ||

      summary?.has_submitted_dossier ||

      summary?.has_ready_for_export_dossier ||

      summary?.has_exported_dossier ||

      summary?.dossier_status === "READY_FOR_EXPORT" ||

      summary?.dossier_status === "EXPORTED",

    );



    setOssSituationModalOpen(false);



    goTo("/hb/oss/property-details", {

      prefillFromDossier: hasDossierPropertyInfo,



      dossierId: summary?.dossier_id || null,



      skipSituationStep: hasDossierPropertyInfo,



      propertySituation: "HAS_PROPERTY",



      requestType: "RENOVATION",



      fromLoanCase: hasLoanCase,



      homebuyerCountry:

        summary?.country_of_property_purchase ||

        summary?.country_of_residence ||

        summary?.country ||

        "",

    });

  }, [dashboardSummary, goTo]);



/* =========================================================*

*     OSS - STILL LOOKING FOR PROPERTY*

*     ========================================================= */



  const handleOssStillLooking = React.useCallback(() => {

    const summary = dashboardSummary || {};



    setOssSituationModalOpen(false);



    goTo("/hb/oss/partners/pre-purchase", {

      dossierId: summary?.dossier_id || null,



      propertySituation: "SEARCHING",



      requestType: "PRE_PURCHASE",



      homebuyerCountry:

        summary?.country_of_residence ||

        summary?.country_of_property_purchase ||

        summary?.country ||

        "",

    });

  }, [dashboardSummary, goTo]);



/* =========================================================*

*     SIDEBAR ITEMS*

*     ========================================================= */



  const items = React.useMemo(

    () => [

      {

        key: "dashboard",



        label: t("items.dashboard"),



        icon: House,



        onClick: () => goTo("/hb/dashboard"),



        isActive: location.pathname === "/hb/dashboard",

      },



      {

        key: "dossier",



        label: t("items.dossier"),



        icon: FolderOpen,



        onClick: handleDossierNavigation,



        isActive: location.pathname.startsWith("/hb/dossier"),

      },



      {

        key: "oss",



        label: t("items.oss"),



        icon: Handshake,



        onClick: handleOssNavigation,



        isActive: location.pathname.startsWith("/hb/oss"),

      },



      {

        key: "documents",



        label: t("items.documents"),



        icon: FileText,



        onClick: () => goTo("/hb/invoices"),



        isActive: location.pathname.startsWith("/hb/invoices"),

      },



      {

        key: "communication",



        label: t("items.communication"),



        icon: MessagesSquare,



        onClick: () => goTo("/hb/communication"),



        isActive: location.pathname.startsWith("/hb/communication"),

      },



      {

        key: "account",



        label: t("items.account"),



        icon: UserPen,



        onClick: () => goTo("/hb/account"),



        isActive: location.pathname.startsWith("/hb/account"),

      },

    ],

    [t, goTo, location.pathname, handleDossierNavigation, handleOssNavigation],

  );



  const resolvedTitle = title || t("title");



  return (

    <>

      {/* =====================================================*

*          DESKTOP SIDEBAR*

*          ===================================================== */}



      <aside className="platform-sidebar">

        <div className="platform-sidebar__panel">

          <div className="platform-sidebar__title">{resolvedTitle}</div>



          <nav

            className="platform-sidebar__nav"

            aria-label={t("aria.desktopNav")}

          >

            {items.map((item) => {

              const Icon = item.icon;



              return (

                <button

                  key={item.key}

                  type="button"

                  onClick={item.onClick}

                  className={`platform-sidebar__item ${

                    item.isActive ? "is-active" : ""

                  }`}

                >

                  <span className="platform-sidebar__icon-wrap">

                    <Icon size={26} strokeWidth={2.1} />
                    {item.key === "communication" && (
                      <span className="legup-communication-unread-icon-overlay">
                        <CommunicationUnreadBadge count={unreadMessagesCount} />
                      </span>
                    )}

                  </span>



                  <span className="platform-sidebar__label">{item.label}</span>
                {item.key === "communication" && (
                    <span className="legup-communication-unread-inline">
                      <CommunicationUnreadBadge count={unreadMessagesCount} />
                    </span>
                  )}

                </button>

              );

            })}

          </nav>

        </div>

      </aside>



      {/* =====================================================*

*          MOBILE OVERLAY*

*          ===================================================== */}



      <div

        className={`platform-sidebar__mobile-overlay ${

          mobileOpen ? "is-open" : ""

        }`}

        onClick={onClose}

      />



      {/* =====================================================*

*          MOBILE SIDEBAR*

*          ===================================================== */}



      <aside

        className={`platform-sidebar__mobile ${mobileOpen ? "is-open" : ""}`}

      >

        <div className="platform-sidebar__mobile-header">

          <span className="platform-sidebar__mobile-title">

            {resolvedTitle}

          </span>



          <button

            type="button"

            className="platform-sidebar__mobile-close"

            onClick={onClose}

            aria-label={t("aria.closeMenu")}

          >

            <X size={22} />

          </button>

        </div>



        <nav

          className="platform-sidebar__mobile-nav"

          aria-label={t("aria.mobileNav")}

        >

          {items.map((item) => {

            const Icon = item.icon;



            return (

              <button

                key={item.key}

                type="button"

                onClick={item.onClick}

                className={`platform-sidebar__mobile-item ${

                  item.isActive ? "is-active" : ""

                }`}

              >

                <span className="platform-sidebar__icon-wrap">

                  <Icon size={24} strokeWidth={2.1} />

                </span>



                <span className="platform-sidebar__mobile-label">

                  {item.label}

                </span>
                {item.key === "communication" && (
                  <CommunicationUnreadBadge count={unreadMessagesCount} />
                )}

              </button>

            );

          })}

        </nav>

      </aside>



      {/* =====================================================*

*          LOCKED DOSSIER MODAL*

*          ===================================================== */}



      {dossierLockedModalOpen && (

        <div

          className={`platform-sidebar__locked-backdrop ${

            dossierLockedModalClosing ? "is-closing" : ""

          }`}

          role="presentation"

          onClick={closeDossierLockedModal}

          onAnimationEnd={(event) => {

            if (

              dossierLockedModalClosing &&

              event.target === event.currentTarget

            ) {

              setDossierLockedModalOpen(false);

              setDossierLockedModalClosing(false);

            }

          }}

        >

          <div

            className={`platform-sidebar__locked-modal ${

              dossierLockedModalClosing ? "is-closing" : ""

            }`}

            role="dialog"

            aria-modal="true"

            aria-labelledby="dossier-locked-title"

            aria-describedby="dossier-locked-description"

            onClick={(event) => event.stopPropagation()}

          >

            <button

              type="button"

              className="platform-sidebar__locked-close"

              onClick={closeDossierLockedModal}

              aria-label={t("dossierLocked.close", {

                defaultValue: "Close",

              })}

            >

              <X size={20} strokeWidth={2} />

            </button>



            <div className="platform-sidebar__locked-icon">

              <LockKeyhole size={29} strokeWidth={2.1} />

            </div>



            <h3

              id="dossier-locked-title"

              className="platform-sidebar__locked-title"

            >

              {t("dossierLocked.title", {

                defaultValue: "Dossier locked",

              })}

            </h3>



            <p

              id="dossier-locked-description"

              className="platform-sidebar__locked-text"

            >

              {t("dossierLocked.message", {

                defaultValue:

                  "Your loan terms have already been agreed with the lender. The information in this dossier is now locked and can no longer be altered.",

              })}

            </p>



            <p className="platform-sidebar__locked-note">

              {t("dossierLocked.note", {

                defaultValue:

                  "If any information needs to be corrected, please contact your lender.",

              })}

            </p>



            <button

              type="button"

              className="platform-sidebar__locked-button"

              onClick={closeDossierLockedModal}

            >

              {t("dossierLocked.button", {

                defaultValue: "Understood",

              })}

            </button>

          </div>

        </div>

      )}



      {/* =====================================================*

*          OSS SITUATION MODAL*

*          ===================================================== */}



      <OssSituationModal

        open={ossSituationModalOpen}

        onClose={() => setOssSituationModalOpen(false)}

        onHasProperty={handleOssHasProperty}

        onStillLooking={handleOssStillLooking}

      />

    </>

  );

}
